import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Building2, Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { AuthLayout } from './AuthLayout';

const signUpSchema = z.object({
  fullName: z.string().min(2, 'Enter your full name'),
  organization: z.string().min(2, 'Organization is required'),
  email: z.string().min(1, 'Email is required').email('Enter a valid work email'),
  password: z
    .string()
    .min(8, 'At least 8 characters')
    .regex(/[A-Z]/, 'Include an uppercase letter')
    .regex(/[a-z]/, 'Include a lowercase letter')
    .regex(/\d/, 'Include a number'),
});

type SignUpValues = z.infer<typeof signUpSchema>;

export function SignUpPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { fullName: '', organization: '', email: '', password: '' },
  });

  const onSubmit = async (_values: SignUpValues) => {
    setSubmitError(null);
    // NexaClaim is a B2B product — accounts are provisioned by your organization admin,
    // not self-serve. This form collects a request; onboarding is handled offline.
    await new Promise((r) => setTimeout(r, 400));
    setSubmitError(
      'Access requests are reviewed by your NexaClaim account manager. ' +
        'If you were invited, check your email for a setup link or contact admin@nexaclaim.ai.',
    );
  };

  return (
    <AuthLayout>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-600">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
        Request access
      </div>

      <h1 className="mt-3 text-[32px] font-semibold leading-tight tracking-tight text-slate-900">
        Create your NexaClaim <span className="text-primary-500">AI</span> account
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
        Tell us a little about your team and we&apos;ll get your denials queue set up.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-8 space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              htmlFor="fullName"
              className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500"
            >
              Full name
            </label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="fullName"
                type="text"
                autoComplete="name"
                placeholder="Jordan Reyes"
                aria-invalid={errors.fullName ? 'true' : 'false'}
                className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15"
                {...register('fullName')}
              />
            </div>
            {errors.fullName && (
              <p className="mt-1.5 text-xs text-rose-600">{errors.fullName.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="organization"
              className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500"
            >
              Organization
            </label>
            <div className="relative">
              <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="organization"
                type="text"
                autoComplete="organization"
                placeholder="University of Utah Health"
                aria-invalid={errors.organization ? 'true' : 'false'}
                className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15"
                {...register('organization')}
              />
            </div>
            {errors.organization && (
              <p className="mt-1.5 text-xs text-rose-600">
                {errors.organization.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500"
          >
            Work email
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@organization.com"
              aria-invalid={errors.email ? 'true' : 'false'}
              className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15"
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-xs text-rose-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500"
          >
            Password
          </label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="8+ chars, mixed case, a number"
              aria-invalid={errors.password ? 'true' : 'false'}
              className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
            >
              {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-rose-600">{errors.password.message}</p>
          )}
        </div>

        {submitError && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="group flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_-12px_rgba(204,0,0,0.55)] transition hover:bg-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? 'Creating account…' : 'Create account'}
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">
        Already using NexaClaim AI?{' '}
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
